import type {
	ElementNode,
	ElementStyle,
} from "@multi-tenant-cms/builder/types";
import type {
	ColumnSetting,
	ContainerElement,
	CustomCodeElement,
	CustomCodeType,
	CustomCodeValidationStatus,
	Element,
	ElementStyles,
} from "@multi-tenant-cms/builder/ui/types";

/**
 * Editor → serialized conversion primitives, shared by the two serializers that
 * need them: `admin/BuilderField/pageBuilderAdapter.ts` (the full ~36-type
 * dispatcher used on save) and `runtime/narrowBuilderNodeSerializer.ts` (the
 * 17-type public-runtime converter). Both must produce byte-identical output for
 * the same input, so these live in one place rather than being mirrored by hand.
 *
 * Deliberately type-only in its imports: this module is pulled into the public
 * region-instance client bundle via the narrow serializer, so it must never grow
 * a value import from `admin/`.
 */

/**
 * Element state → the prop marker its styles are serialized under.
 *
 * `button` and `form_submit` are mirrors of each other (see `ButtonProps`), and the mirror was
 * previously half-built: `__hoverStylesJson` was written by BOTH serializers and read by nothing
 * outside the adapter, so every hover style set on a button was published as nothing. Keeping the
 * names and the writer here means a new state is added once.
 */
export const BUTTON_STATE_STYLE_MARKERS = {
	hoverStyles: "__hoverStylesJson",
	focusStyles: "__focusStylesJson",
	activeStyles: "__activeStylesJson",
} as const satisfies Record<string, string>;

export type ButtonStateStyleKey = keyof typeof BUTTON_STATE_STYLE_MARKERS;

/** Writes every non-empty state-style bag onto `props` under its marker. */
export function applyButtonStateStyleProps(
	element: Partial<Record<ButtonStateStyleKey, ElementStyles | undefined>>,
	props: Record<string, unknown>
): void {
	for (const [key, marker] of Object.entries(BUTTON_STATE_STYLE_MARKERS)) {
		const bag = element[key as ButtonStateStyleKey];
		if (bag && Object.keys(bag).length > 0) {
			props[marker] = JSON.stringify(bag);
		}
	}
}

/** Inverse of {@link applyButtonStateStyleProps} — parses each marker back onto the element. */
export function readButtonStateStyleProps(
	props: Record<string, unknown>
): Partial<Record<ButtonStateStyleKey, ElementStyles>> {
	const out: Partial<Record<ButtonStateStyleKey, ElementStyles>> = {};
	for (const [key, marker] of Object.entries(BUTTON_STATE_STYLE_MARKERS)) {
		const raw = props[marker];
		if (typeof raw !== "string" || !raw.trim()) {
			continue;
		}
		try {
			const parsed = JSON.parse(raw) as unknown;
			if (
				typeof parsed === "object" &&
				parsed !== null &&
				!Array.isArray(parsed)
			) {
				out[key as ButtonStateStyleKey] = parsed as ElementStyles;
			}
		} catch {
			// A malformed bag is dropped, not thrown — one bad style must not fail a page load.
		}
	}
	return out;
}

export function asValue(value?: string): { value: string } | undefined {
	if (!value) {
		return undefined;
	}
	return { value };
}

export function normalizedTextTransform(
	raw: string | undefined
): "none" | "uppercase" | "lowercase" | "capitalize" | undefined {
	const tt = raw?.trim().toLowerCase();
	if (
		tt === "uppercase" ||
		tt === "lowercase" ||
		tt === "capitalize" ||
		tt === "none"
	) {
		return tt;
	}
	return undefined;
}

export function toBoxRef(value?: string):
	| {
			top: { value: string };
			right: { value: string };
			bottom: { value: string };
			left: { value: string };
	  }
	| undefined {
	if (!value) {
		return undefined;
	}
	if (/\s/.test(value.trim())) {
		return undefined;
	}
	const ref = { value };
	return { top: ref, right: ref, bottom: ref, left: ref };
}

export function toSpacingRef(v?: string): { value: string } | undefined {
	if (v == null || v === "") {
		return undefined;
	}
	return { value: String(v) };
}

/** Map flat ElementStyles.display into builder layout.display (inline-block etc. must round-trip for buttons). */
export function mapElementDisplayToLayout(
	display: string | undefined
):
	| "block"
	| "flex"
	| "inline"
	| "inline-block"
	| "inline-flex"
	| "grid"
	| "none"
	| undefined {
	if (!display) {
		return undefined;
	}
	const d = display.trim().toLowerCase();
	if (
		d === "flex" ||
		d === "block" ||
		d === "inline" ||
		d === "inline-block" ||
		d === "inline-flex" ||
		d === "grid" ||
		d === "none"
	) {
		return d as
			| "block"
			| "flex"
			| "inline"
			| "inline-block"
			| "inline-flex"
			| "grid"
			| "none";
	}
	return undefined;
}

export function toBuilderStyle(
	styles: ElementStyles | undefined
): ElementStyle | undefined {
	if (!styles) {
		return undefined;
	}

	const hasPerSideMargin =
		styles.marginTop != null ||
		styles.marginRight != null ||
		styles.marginBottom != null ||
		styles.marginLeft != null;
	const hasPerSidePadding =
		styles.paddingTop != null ||
		styles.paddingRight != null ||
		styles.paddingBottom != null ||
		styles.paddingLeft != null;

	let margin:
		| {
				top?: { value: string };
				right?: { value: string };
				bottom?: { value: string };
				left?: { value: string };
		  }
		| undefined = hasPerSideMargin
		? {
				top: toSpacingRef(styles.marginTop),
				right: toSpacingRef(styles.marginRight),
				bottom: toSpacingRef(styles.marginBottom),
				left: toSpacingRef(styles.marginLeft),
			}
		: toBoxRef(styles.margin);
	// Match Canvas / SettingsPanel: block alignment uses margin auto even when no margin shorthand exists.
	// Otherwise BuilderPreview (template thumbnails, public page) loses centering vs the editor iframe.
	if (styles.layoutAlign === "center") {
		margin = {
			...(margin || {}),
			left: { value: "auto" },
			right: { value: "auto" },
		};
	} else if (styles.layoutAlign === "right") {
		margin = { ...(margin || {}), left: { value: "auto" } };
	}
	const padding = hasPerSidePadding
		? {
				top: toSpacingRef(styles.paddingTop),
				right: toSpacingRef(styles.paddingRight),
				bottom: toSpacingRef(styles.paddingBottom),
				left: toSpacingRef(styles.paddingLeft),
			}
		: toBoxRef(styles.padding);

	const hasLayout =
		styles.width ||
		styles.height ||
		styles.minWidth ||
		styles.maxWidth ||
		styles.maxHeight ||
		styles.minHeight ||
		styles.display ||
		styles.float ||
		styles.position ||
		styles.zIndex ||
		styles.overflow ||
		styles.left ||
		styles.right ||
		styles.top ||
		styles.bottom ||
		styles.flexDirection ||
		styles.justifyContent ||
		styles.alignItems ||
		styles.gap ||
		styles.objectFit ||
		styles.objectPosition ||
		styles.layoutAlign;

	const layout: ElementStyle["layout"] = hasLayout
		? {
				width: styles.width ? { desktop: styles.width } : undefined,
				height: styles.height ? { desktop: styles.height } : undefined,
				minWidth: styles.minWidth ? { desktop: styles.minWidth } : undefined,
				maxWidth: styles.maxWidth ? { desktop: styles.maxWidth } : undefined,
				maxHeight: styles.maxHeight ? { desktop: styles.maxHeight } : undefined,
				minHeight: styles.minHeight ? { desktop: styles.minHeight } : undefined,
				objectFit: styles.objectFit ? { desktop: styles.objectFit } : undefined,
				objectPosition: styles.objectPosition
					? { desktop: styles.objectPosition }
					: undefined,
				display: mapElementDisplayToLayout(styles.display),
				float: styles.float ? { desktop: styles.float } : undefined,
				position: styles.position ? { desktop: styles.position } : undefined,
				zIndex: styles.zIndex ? { desktop: styles.zIndex } : undefined,
				overflow: styles.overflow ? { desktop: styles.overflow } : undefined,
				left: styles.left ? { desktop: styles.left } : undefined,
				right: styles.right ? { desktop: styles.right } : undefined,
				top: styles.top ? { desktop: styles.top } : undefined,
				bottom: styles.bottom ? { desktop: styles.bottom } : undefined,
				flexDirection: styles.flexDirection
					? {
							desktop: styles.flexDirection,
						}
					: undefined,
				justifyContent: styles.justifyContent
					? {
							desktop: styles.justifyContent as
								| "flex-start"
								| "center"
								| "space-between"
								| "space-around"
								| "flex-end",
						}
					: undefined,
				alignItems: styles.alignItems
					? {
							desktop: styles.alignItems as
								| "flex-start"
								| "center"
								| "flex-end"
								| "stretch",
						}
					: undefined,
				gap: styles.gap ? { desktop: asValue(styles.gap) } : undefined,
			}
		: undefined;

	const hasTypography =
		styles.color ||
		styles.fontSize ||
		styles.fontWeight ||
		styles.lineHeight ||
		styles.textAlign ||
		styles.fontFamily ||
		styles.fontStyle ||
		styles.letterSpacing ||
		styles.textTransform ||
		styles.textDecoration ||
		styles.backgroundClip ||
		styles.WebkitBackgroundClip ||
		styles.WebkitTextFillColor;

	const textTransform = normalizedTextTransform(styles.textTransform);

	const typography: ElementStyle["typography"] = hasTypography
		? {
				color: asValue(styles.color),
				fontFamily: styles.fontFamily ? asValue(styles.fontFamily) : undefined,
				fontSize: styles.fontSize
					? { desktop: asValue(styles.fontSize) }
					: undefined,
				fontWeight: asValue(styles.fontWeight),
				lineHeight: asValue(styles.lineHeight),
				letterSpacing: styles.letterSpacing
					? asValue(styles.letterSpacing)
					: undefined,
				...(textTransform ? { textTransform } : {}),
				textAlign: styles.textAlign ? { desktop: styles.textAlign } : undefined,
				...(styles.fontStyle?.trim()
					? { fontStyle: styles.fontStyle.trim() }
					: {}),
				...(styles.textDecoration?.trim()
					? { textDecoration: styles.textDecoration.trim() }
					: {}),
				...(styles.backgroundClip?.trim()
					? { backgroundClip: styles.backgroundClip.trim() }
					: {}),
				...(styles.WebkitBackgroundClip?.trim()
					? { webkitBackgroundClip: styles.WebkitBackgroundClip.trim() }
					: {}),
				...(styles.WebkitTextFillColor?.trim()
					? { webkitTextFillColor: styles.WebkitTextFillColor.trim() }
					: {}),
			}
		: undefined;

	const hasBackground =
		!!styles.backgroundColor?.trim() || !!styles.backgroundImage?.trim();

	const background: ElementStyle["background"] = hasBackground
		? {
				...(styles.backgroundColor?.trim()
					? { color: asValue(styles.backgroundColor) }
					: {}),
				...(styles.backgroundImage?.trim()
					? { gradient: styles.backgroundImage.trim() }
					: {}),
			}
		: undefined;

	const result: ElementStyle = {
		spacing:
			margin || padding
				? {
						margin: margin ? { desktop: margin } : undefined,
						padding: padding ? { desktop: padding } : undefined,
					}
				: undefined,
		typography,
		background,
		border:
			styles.borderRadius ||
			styles.borderWidth ||
			styles.borderStyle ||
			styles.borderColor
				? {
						radius: styles.borderRadius
							? { desktop: asValue(styles.borderRadius) }
							: undefined,
						width: styles.borderWidth
							? { desktop: asValue(styles.borderWidth) }
							: undefined,
						style: styles.borderStyle
							? {
									desktop: styles.borderStyle as
										| "none"
										| "solid"
										| "dashed"
										| "dotted",
								}
							: undefined,
						color: asValue(styles.borderColor),
					}
				: undefined,
		layout,
		effects:
			styles.opacity ||
			styles.boxShadow ||
			(styles.animation && String(styles.animation).trim()) ||
			(styles.transition && String(styles.transition).trim()) ||
			(styles.transform && String(styles.transform).trim())
				? {
						opacity:
							styles.opacity == null
								? undefined
								: { desktop: Number(styles.opacity) },
						boxShadow: styles.boxShadow
							? { desktop: asValue(styles.boxShadow) }
							: undefined,
						...(styles.animation && String(styles.animation).trim()
							? { animation: String(styles.animation).trim() }
							: {}),
						...(styles.transition && String(styles.transition).trim()
							? { transition: String(styles.transition).trim() }
							: {}),
						...(styles.transform && String(styles.transform).trim()
							? { transform: String(styles.transform).trim() }
							: {}),
					}
				: undefined,
	};

	return result;
}

// ---- DOM identity + legacy-styles wrapping (verbatim from pageBuilderAdapter.ts) ----

export function domIdentityPayload(el: Element): {
	domClass?: string;
	domId?: string;
	customAttributes?: Record<string, string>;
} {
	const rawDomId = (el as { domId?: string }).domId;
	const domId = typeof rawDomId === "string" ? rawDomId.trim() : "";
	const clsArr = (el as { customClasses?: string[] }).customClasses;
	const domClass =
		Array.isArray(clsArr) && clsArr.length > 0
			? clsArr.filter(Boolean).join(" ").trim()
			: "";
	const o: {
		domClass?: string;
		domId?: string;
		customAttributes?: Record<string, string>;
	} = {};
	if (domId) {
		o.domId = domId;
	}
	if (domClass) {
		o.domClass = domClass;
	}
	// Previously destructured to `_ca` and dropped in every save case, so heading
	// highlight and dark-mode typography colour (stored here by
	// `HeadingMainOptionsSection`) were silently lost on save.
	const attrs = (el as { customAttributes?: Record<string, string> })
		.customAttributes;
	if (attrs && typeof attrs === "object" && !Array.isArray(attrs)) {
		const kept: Record<string, string> = {};
		for (const [key, value] of Object.entries(attrs)) {
			if (key.trim() && typeof value === "string") {
				kept[key] = value;
			}
		}
		if (Object.keys(kept).length > 0) {
			o.customAttributes = kept;
		}
	}
	return o;
}

export function withDom(el: Element, node: ElementNode): ElementNode {
	const dom = domIdentityPayload(el);
	if (!(dom.domId || dom.domClass || dom.customAttributes)) {
		return node;
	}
	return { ...node, ...dom };
}

export function attachLegacyFlatStyles(
	element: Element,
	node: ElementNode
): ElementNode {
	const styles = element.styles;
	if (!styles || Object.keys(styles).length === 0) {
		return node;
	}
	return { ...node, styles } as ElementNode & { styles?: ElementStyles };
}

// ---- prop-shape types for the container-flavored cases (verbatim from pageBuilderAdapter.ts) ----

/** Props we attach to a container node that was built from a columns element, so we can restore it on load */
export type BuilderColumnsProps = {
	tag?: "section" | "div";
	__builderColumns?: number;
	__builderColumnWidths?: string[];
	__builderColumnSettings?: ColumnSetting[];
	__builderColumnsAlign?: string;
	__builderColumnsJustify?: string;
	__builderColumnsWrap?: boolean;
	/** Equal-width columns per wrap row when wrap is on (1 = stack). */
	__builderColumnsWrapColumns?: number;
	__builderColumnsGap?: string;
	__builderResponsiveColumnsLayout?: import("@multi-tenant-cms/builder/ui/types").ResponsiveColumnsLayoutOverrides;
	__builderContentWidth?: "fullViewport";
};

export type BuilderNavigationMenuProps = {
	tag?: "div";
	__builderNavigationMenu?: true;
	__menuId?: string;
	__menuName?: string;
	__menuOrientation?: "horizontal" | "vertical";
	__navLinkColor?: string;
	__navLinkHoverColor?: string;
	__navLinkActiveColor?: string;
	__menuItems?: Array<{
		id: string;
		label: string;
		href: string;
		openInNewTab: boolean;
	}>;
};

export type BuilderCustomCodeProps = {
	tag?: "div";
	__builderCustomCode?: true;
	__codeType?: CustomCodeType;
	__sourceCode?: string;
	__cssCode?: string;
	__jsCode?: string;
	__compiledCode?: string;
	__componentId?: string;
	__compiledAt?: string;
	__validationStatus?: CustomCodeValidationStatus;
	__validationMessages?: string[];
	__dependencies?: string[];
	__componentName?: string;
	__staticHtml?: string;
	__parkedCode?: CustomCodeElement["parkedCode"];
};

export type BuilderFormEmbedProps = {
	tag?: "div";
	__builderFormEmbed?: true;
	__formEmbedId?: string;
};

/**
 * `tag` plus the `__builder*` props that carry semantics the narrow `tag` field
 * cannot. Shared so the admin save path and the public-runtime path cannot
 * disagree about a container's tag, link or typography opt-out.
 */
export function containerSemanticProps(
	el: Pick<
		ContainerElement,
		| "tag"
		| "href"
		| "target"
		| "ownTypography"
		| "hoverStyles"
		| "focusStyles"
		| "activeStyles"
	>
): Record<string, unknown> {
	const tag = el.tag || "div";
	const props: Record<string, unknown> = {
		tag: (tag === "section" ? "section" : "div") as "div" | "section",
	};
	if (tag !== "div" && tag !== "section") {
		props.__builderContainerTag = tag;
	}
	if (el.ownTypography) {
		props.__builderOwnTypography = true;
	}
	applyButtonStateStyleProps(el, props);
	const href = tag === "a" && typeof el.href === "string" ? el.href.trim() : "";
	if (href) {
		props.__builderContainerHref = href;
		if (el.target === "_blank" || el.target === "_self") {
			props.__builderContainerTarget = el.target;
		}
	}
	return props;
}
